import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { RootStore } from './RootStore';
import axios from 'axios';
import { API } from '../constants';

export interface Vehicle {
    id: number;
    license_plate: string;
    brand: string;
    model: string;
    year: number;
    color: string;
    vin: string;
    engine_number: string;
    airtag_number: string;
    status: 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE';
    status_display: string;
    documents_count: string;
    has_airtag: string;
    acquisition_date: string;
    image_url?: string;
    current_location_lat?: string;
    current_location_lng?: string;
    last_location_update?: string;
    created_at: string;
    updated_at: string;
}

export interface VehicleFormData {
    license_plate: string;
    brand: string;
    model: string;
    year: string;
    color: string;
    vin: string;
    engine_number: string;
    acquisition_date: string;
    airtag_number: string;
    status?: 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE';
    image_url?: string;
}

export class VehicleStore {
    vehicles: Vehicle[] = [];
    isLoading = false;
    isSubmitting = false;
    error: string | null = null;

    private rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this, {
            vehicles: observable,
            isLoading: observable,
            isSubmitting: observable,
            error: observable,
            createVehicle: action,
            updateVehicle: action,
            deleteVehicle: action,
            loadVehicles: action,
            clearError: action,
            setError: action,
            activeVehicles: computed
        });
    }

    get activeVehicles() {
        return this.vehicles.filter(vehicle => vehicle.status === 'ACTIVE');
    }

    setError = (error: string) => {
        this.error = error;
    };

    clearError = () => {
        this.error = null;
    };

    loadVehicles = async () => {
        this.isLoading = true;
        this.error = null;

        try {
            const response = await axios.get(`${API.BASE_URL.DEVELOPMENT}/vehicles/`);
            runInAction(() => {
                this.vehicles = response.data.results || response.data || [];
                this.isLoading = false;
            });
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.detail || 'Error al cargar vehículos';
                this.isLoading = false;
            });
        }
    };

    createVehicle = async (vehicleData: VehicleFormData): Promise<boolean> => {
        this.isSubmitting = true;
        this.error = null;

        try {

            const formData = new FormData();
            formData.append('license_plate', vehicleData.license_plate);
            formData.append('brand', vehicleData.brand);
            formData.append('model', vehicleData.model);
            formData.append('year', vehicleData.year);
            formData.append('color', vehicleData.color);
            formData.append('vin', vehicleData.vin);
            formData.append('engine_number', vehicleData.engine_number);
            formData.append('acquisition_date', vehicleData.acquisition_date);
            formData.append('airtag_number', vehicleData.airtag_number);
            formData.append('status', vehicleData.status || 'ACTIVE');

            if (vehicleData.image_url && vehicleData.image_url.trim()) {
                formData.append('image_url', vehicleData.image_url);
            }

            const response = await axios.post(`${API.BASE_URL.DEVELOPMENT}/vehicles/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            runInAction(() => {

                this.vehicles.unshift(response.data);
                this.isSubmitting = false;
            });

            return true;
        } catch (error: any) {
            runInAction(() => {
                if (error.response?.data) {

                    if (error.response.data.license_plate) {
                        this.error = `Placa: ${error.response.data.license_plate[0]}`;
                    } else if (error.response.data.vin) {
                        this.error = `VIN: ${error.response.data.vin[0]}`;
                    } else if (error.response.data.engine_number) {
                        this.error = `Número de motor: ${error.response.data.engine_number[0]}`;
                    } else if (error.response.data.acquisition_date) {
                        this.error = `Fecha de adquisición: ${error.response.data.acquisition_date[0]}`;
                    } else if (error.response.data.airtag_number) {
                        this.error = `Número de AirTag: ${error.response.data.airtag_number[0]}`;
                    } else if (error.response.data.detail) {
                        this.error = error.response.data.detail;
                    } else if (error.response.data.non_field_errors) {
                        this.error = error.response.data.non_field_errors[0];
                    } else {
                        this.error = 'Error al crear el vehículo';
                    }
                } else {
                    this.error = 'Error de conexión. Intenta nuevamente.';
                }
                this.isSubmitting = false;
            });
            return false;
        }
    };


    updateVehicle = async (vehicleId: number, vehicleData: VehicleFormData): Promise<boolean> => {
        this.isSubmitting = true;
        this.error = null;

        try {
            const formData = new FormData();
            formData.append('license_plate', vehicleData.license_plate);
            formData.append('brand', vehicleData.brand);
            formData.append('model', vehicleData.model);
            formData.append('year', vehicleData.year);
            formData.append('color', vehicleData.color);
            formData.append('vin', vehicleData.vin);
            formData.append('engine_number', vehicleData.engine_number);
            formData.append('acquisition_date', vehicleData.acquisition_date);
            formData.append('airtag_number', vehicleData.airtag_number);
            formData.append('status', vehicleData.status || 'ACTIVE');

            if (vehicleData.image_url && vehicleData.image_url.trim()) {
                formData.append('image_url', vehicleData.image_url);
            }

            const response = await axios.put(`${API.BASE_URL.DEVELOPMENT}/vehicles/${vehicleId}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            runInAction(() => {
                const index = this.vehicles.findIndex(v => v.id === vehicleId);
                if (index !== -1) {
                    this.vehicles[index] = response.data;
                }
                this.isSubmitting = false;
            });

            return true;
        } catch (error: any) {
            runInAction(() => {
                if (error.response?.data) {
                    if (error.response.data.license_plate) {
                        this.error = `Placa: ${error.response.data.license_plate[0]}`;
                    } else if (error.response.data.vin) {
                        this.error = `VIN: ${error.response.data.vin[0]}`;
                    } else if (error.response.data.engine_number) {
                        this.error = `Número de motor: ${error.response.data.engine_number[0]}`;
                    } else if (error.response.data.acquisition_date) {
                        this.error = `Fecha de adquisición: ${error.response.data.acquisition_date[0]}`;
                    } else if (error.response.data.airtag_number) {
                        this.error = `Número de AirTag: ${error.response.data.airtag_number[0]}`;
                    } else if (error.response.data.detail) {
                        this.error = error.response.data.detail;
                    } else if (error.response.data.non_field_errors) {
                        this.error = error.response.data.non_field_errors[0];
                    } else {
                        this.error = 'Error al actualizar el vehículo';
                    }
                } else {
                    this.error = 'Error de conexión. Intenta nuevamente.';
                }
                this.isSubmitting = false;
            });
            return false;
        }
    };

    deleteVehicle = async (vehicleId: number): Promise<boolean> => {
        this.isSubmitting = true;
        this.error = null;

        try {
            await axios.delete(`${API.BASE_URL.DEVELOPMENT}/vehicles/${vehicleId}/`);
            
            runInAction(() => {
                this.vehicles = this.vehicles.filter(v => v.id !== vehicleId);
                this.isSubmitting = false;
            });

            return true;
        } catch (error: any) {
            runInAction(() => {
                if (error.response?.data?.detail) {
                    this.error = error.response.data.detail;
                } else {
                    this.error = 'Error al eliminar el vehículo';
                }
                this.isSubmitting = false;
            });
            return false;
        }
    };

    getVehicleById = (vehicleId: number): Vehicle | undefined => {
        return this.vehicles.find(vehicle => vehicle.id === vehicleId);
    };

    validateVehicleForm = (formData: VehicleFormData): { isValid: boolean; errors: Record<string, string> } => {
        const errors: Record<string, string> = {};


        if (!formData.license_plate.trim()) {
            errors.license_plate = 'La placa es requerida';
        } else if (formData.license_plate.length > 10) {
            errors.license_plate = 'La placa no puede tener más de 10 caracteres';
        } else if (!/^[A-Z0-9-]+$/i.test(formData.license_plate)) {
            errors.license_plate = 'La placa solo puede contener letras, números y guiones';
        }


        if (!formData.brand.trim()) {
            errors.brand = 'La marca es requerida';
        } else if (formData.brand.length > 50) {
            errors.brand = 'La marca no puede tener más de 50 caracteres';
        }


        if (!formData.model.trim()) {
            errors.model = 'El modelo es requerido';
        } else if (formData.model.length > 50) {
            errors.model = 'El modelo no puede tener más de 50 caracteres';
        }


        if (!formData.year || !formData.year.trim()) {
            errors.year = 'El año es requerido';
        } else {
            const year = parseInt(formData.year);
            const currentYear = new Date().getFullYear();
            if (isNaN(year) || year < 1900 || year > currentYear + 1) {
                errors.year = `El año debe estar entre 1900 y ${currentYear + 1}`;
            }
        }


        if (!formData.color.trim()) {
            errors.color = 'El color es requerido';
        } else if (formData.color.length > 30) {
            errors.color = 'El color no puede tener más de 30 caracteres';
        }


        if (!formData.vin.trim()) {
            errors.vin = 'El VIN es requerido';
        } else if (formData.vin.length > 50) {
            errors.vin = 'El VIN no puede tener más de 50 caracteres';
        } else if (!/^[A-Z0-9]+$/i.test(formData.vin)) {
            errors.vin = 'El VIN solo puede contener letras y números';
        }


        if (!formData.engine_number.trim()) {
            errors.engine_number = 'El número de motor es requerido';
        } else if (formData.engine_number.length > 50) {
            errors.engine_number = 'El número de motor no puede tener más de 50 caracteres';
        }


        if (!formData.acquisition_date.trim()) {
            errors.acquisition_date = 'La fecha de adquisición es requerida';
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.acquisition_date)) {
            errors.acquisition_date = 'La fecha debe tener el formato YYYY-MM-DD';
        } else {
            const date = new Date(formData.acquisition_date);
            if (isNaN(date.getTime())) {
                errors.acquisition_date = 'La fecha no es válida';
            } else if (date > new Date()) {
                errors.acquisition_date = 'La fecha de adquisición no puede ser futura';
            }
        }

        if (!formData.airtag_number.trim()) {
            errors.airtag_number = 'El número de AirTag es requerido';
        } else if (formData.airtag_number.length > 50) {
            errors.airtag_number = 'El número de AirTag no puede tener más de 50 caracteres';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };
}
